import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    Divider,
    Switch,
    FormControlLabel,
    CircularProgress,
    IconButton,
    Tabs,
    Tab,
    Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import mizanSettingsAPI from "../../Network/MizanSettings";

const cookies = new Cookies();

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function MizanSettingsDialog({ open, setOpen, mizan }) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [isNew, setIsNew] = useState(true);

    // Settings state
    const [settings, setSettings] = useState({
        mizan_list_id: null,
        customer_name: "",
        customer_code: "",
        wialon_name: "",
        transaction_source: "",
        // Database settings
        database_name: "",
        server_ip: "",
        database_username: "",
        database_password: "",
        // Camera settings
        camera_one_ip: "",
        camera_two_ip: "",
        camera_username: "",
        camera_pw: "",
        camera_mandatory: false,
        enable_camera: false,
        // Weight settings
        weight_com: "",
        weight_msg_format: "",
        weight_min_accepted_value: 1000,
        weight_reset_min_value: 100,
        weight_reset_min_time_out_value: 3,
        weight_state: false,
        bypass_steady_state: false,
        // RFID settings
        rfid_com: "",
        rfid_state: false,
        two_rfid_state: false,
        rfid_hopeland_ip_one: "",
        rfid_hopeland_username_one: "",
        rfid_hopeland_password_one: "",
        rfid_hopeland_ip_two: "",
        rfid_hopeland_username_two: "",
        rfid_hopeland_password_two: "",
        // General settings
        auto_start: false,
        enable_logging: false,
        destination_folder_for_logging: "",
        export_d_folder: "",
        simulation_mode: false,
        // Environment info (read-only)
        app_version: "",
        windows_os_version: "",
        java_version: "",
        sql_server_version: "",
        dotnet_framework_version: "",
        sql_client_version: "",
        last_environment_sync: null,
    });

    useEffect(() => {
        if (open && mizan?.id) {
            loadSettings();
        }
    }, [open, mizan]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const result = await mizanSettingsAPI.getMizanSettings({
                mizanListId: mizan.id,
                params: {
                    username: cookies.get("user_name"),
                    user_id: cookies.get("user_id"),
                    account_id: cookies.get("account_id"),
                    sid: cookies.get("sid"),
                }
            });

            if (result.status && result?.data?.status && result?.data?.data?.message) {
                setSettings(prev => ({ ...prev, ...result.data.data.message, mizan_list_id: mizan.id }));
                setIsNew(false);
            } else {
                // No settings exist yet
                setSettings(prev => ({ ...prev, mizan_list_id: mizan.id }));
                setIsNew(true);
            }
        } catch (error) {
            console.error("Error loading settings:", error);
        }
        setLoading(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const dataToSend = { ...settings, mizan_list_id: mizan.id };

            const result = isNew
                ? await mizanSettingsAPI.saveMizanSettings({
                    data: dataToSend,
                    params: {
                        username: cookies.get("user_name"),
                        user_id: cookies.get("user_id"),
                        account_id: cookies.get("account_id"),
                        sid: cookies.get("sid"),
                    }
                })
                : await mizanSettingsAPI.updateMizanSettings({
                    mizanListId: mizan.id,
                    data: dataToSend,
                    params: {
                        username: cookies.get("user_name"),
                        user_id: cookies.get("user_id"),
                        account_id: cookies.get("account_id"),
                        sid: cookies.get("sid"),
                    }
                });

            if (result.status && result?.data?.status) {
                toast.success(t("NETWORK_MESSAGES.saveSuccess") || "تم الحفظ بنجاح");
                setIsNew(false);
            } else {
                toast.error(result?.data?.data?.message || t("NETWORK_MESSAGES.unknownError"));
            }
        } catch (error) {
            toast.error(t("NETWORK_MESSAGES.unknownError"));
        }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false);
        setTabValue(0);
    };

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen
            PaperProps={{
                sx: {
                    background: maintheme?.paperColor || '#f5f5f5',
                    '& .MuiTextField-root': {
                        fontFamily: 'Cairo-Medium'
                    },
                    '& .MuiInputLabel-root': {
                        fontFamily: 'Cairo-Medium'
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    },
                    '& .MuiInputBase-input::placeholder': {
                        fontFamily: 'Cairo-Medium'
                    },
                    '& .MuiFormControlLabel-label': {
                        fontFamily: 'Cairo-Medium'
                    },
                    '& .MuiTypography-root': {
                        fontFamily: 'Cairo-Medium'
                    },
                    '& .MuiButton-root': {
                        fontFamily: 'Cairo-Medium'
                    }
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: maintheme?.primary || '#1976d2',
                color: '#fff',
                py: 2,
                fontFamily: 'Cairo-Bold'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Cairo-Bold' }}>
                        {t("MIZAN_LIST.settings") || "إعدادات الميزان"}:
                    </Typography>
                    <Typography variant="h5" sx={{ fontFamily: 'Cairo-Medium' }}>
                        {mizan?.display_name || ''} {mizan?.code ? `(${mizan.code})` : ''}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Paper sx={{ borderRadius: 0 }}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, v) => setTabValue(v)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    '& .MuiTab-root': {
                                        fontFamily: 'Cairo-Medium',
                                        fontSize: '14px'
                                    }
                                }}
                            >
                                <Tab label={t("MIZAN_SETTINGS.general") || "عام"} />
                                <Tab label={t("MIZAN_SETTINGS.database") || "قاعدة البيانات"} />
                                <Tab label={t("MIZAN_SETTINGS.camera") || "الكاميرا"} />
                                <Tab label={t("MIZAN_SETTINGS.weight") || "الوزن"} />
                                <Tab label={t("MIZAN_SETTINGS.rfid") || "RFID"} />
                                <Tab label={t("MIZAN_SETTINGS.environment") || "البيئة"} />
                            </Tabs>
                        </Paper>

                        {/* General Tab */}
                        <TabPanel value={tabValue} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.customer_name") || "اسم العميل"}
                                        value={settings.customer_name || ""}
                                        onChange={handleChange("customer_name")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.customer_code") || "كود العميل"}
                                        value={settings.customer_code || ""}
                                        onChange={handleChange("customer_code")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.wialon_name") || "اسم Wialon"}
                                        value={settings.wialon_name || ""}
                                        onChange={handleChange("wialon_name")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.transaction_source") || "مصدر العملية"}
                                        value={settings.transaction_source || ""}
                                        onChange={handleChange("transaction_source")}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.auto_start || false} onChange={handleChange("auto_start")} />}
                                        label={t("MIZAN_SETTINGS.auto_start") || "بدء تلقائي"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.enable_logging || false} onChange={handleChange("enable_logging")} />}
                                        label={t("MIZAN_SETTINGS.enable_logging") || "تفعيل التسجيل"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.simulation_mode || false} onChange={handleChange("simulation_mode")} />}
                                        label={t("MIZAN_SETTINGS.simulation_mode") || "وضع المحاكاة"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.export_folder") || "مجلد التصدير"}
                                        value={settings.export_d_folder || ""}
                                        onChange={handleChange("export_d_folder")}
                                    />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Database Tab */}
                        <TabPanel value={tabValue} index={1}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.server_ip") || "عنوان الخادم"}
                                        value={settings.server_ip || ""}
                                        onChange={handleChange("server_ip")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.database_name") || "اسم قاعدة البيانات"}
                                        value={settings.database_name || ""}
                                        onChange={handleChange("database_name")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.database_username") || "اسم المستخدم"}
                                        value={settings.database_username || ""}
                                        onChange={handleChange("database_username")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label={t("MIZAN_SETTINGS.database_password") || "كلمة المرور"}
                                        value={settings.database_password || ""}
                                        onChange={handleChange("database_password")}
                                    />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Camera Tab */}
                        <TabPanel value={tabValue} index={2}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.enable_camera || false} onChange={handleChange("enable_camera")} />}
                                        label={t("MIZAN_SETTINGS.enable_camera") || "تفعيل الكاميرا"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.camera_mandatory || false} onChange={handleChange("camera_mandatory")} />}
                                        label={t("MIZAN_SETTINGS.camera_mandatory") || "الكاميرا إجبارية"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.camera_one_ip") || "IP الكاميرا 1"}
                                        value={settings.camera_one_ip || ""}
                                        onChange={handleChange("camera_one_ip")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.camera_two_ip") || "IP الكاميرا 2"}
                                        value={settings.camera_two_ip || ""}
                                        onChange={handleChange("camera_two_ip")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.camera_username") || "اسم مستخدم الكاميرا"}
                                        value={settings.camera_username || ""}
                                        onChange={handleChange("camera_username")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label={t("MIZAN_SETTINGS.camera_pw") || "كلمة مرور الكاميرا"}
                                        value={settings.camera_pw || ""}
                                        onChange={handleChange("camera_pw")}
                                    />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Weight Tab */}
                        <TabPanel value={tabValue} index={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.weight_state || false} onChange={handleChange("weight_state")} />}
                                        label={t("MIZAN_SETTINGS.weight_state") || "تفعيل الميزان"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.bypass_steady_state || false} onChange={handleChange("bypass_steady_state")} />}
                                        label={t("MIZAN_SETTINGS.bypass_steady_state") || "تجاوز الاستقرار"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.weight_com") || "منفذ الميزان"}
                                        value={settings.weight_com || ""}
                                        onChange={handleChange("weight_com")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.weight_msg_format") || "تنسيق رسالة الوزن"}
                                        value={settings.weight_msg_format || ""}
                                        onChange={handleChange("weight_msg_format")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t("MIZAN_SETTINGS.weight_min_accepted_value") || "الحد الأدنى للوزن"}
                                        value={settings.weight_min_accepted_value || ""}
                                        onChange={handleChange("weight_min_accepted_value")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t("MIZAN_SETTINGS.weight_reset_min_value") || "قيمة إعادة التعيين"}
                                        value={settings.weight_reset_min_value || ""}
                                        onChange={handleChange("weight_reset_min_value")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t("MIZAN_SETTINGS.weight_reset_timeout") || "مهلة إعادة التعيين"}
                                        value={settings.weight_reset_min_time_out_value || ""}
                                        onChange={handleChange("weight_reset_min_time_out_value")}
                                    />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* RFID Tab */}
                        <TabPanel value={tabValue} index={4}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.rfid_state || false} onChange={handleChange("rfid_state")} />}
                                        label={t("MIZAN_SETTINGS.rfid_state") || "تفعيل RFID"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.two_rfid_state || false} onChange={handleChange("two_rfid_state")} />}
                                        label={t("MIZAN_SETTINGS.two_rfid_state") || "قارئين RFID"}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.rfid_com") || "منفذ RFID"}
                                        value={settings.rfid_com || ""}
                                        onChange={handleChange("rfid_com")}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography variant="subtitle2">Hopeland 1</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="IP"
                                        value={settings.rfid_hopeland_ip_one || ""}
                                        onChange={handleChange("rfid_hopeland_ip_one")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Username"
                                        value={settings.rfid_hopeland_username_one || ""}
                                        onChange={handleChange("rfid_hopeland_username_one")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="Password"
                                        value={settings.rfid_hopeland_password_one || ""}
                                        onChange={handleChange("rfid_hopeland_password_one")}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider><Typography variant="subtitle2">Hopeland 2</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="IP"
                                        value={settings.rfid_hopeland_ip_two || ""}
                                        onChange={handleChange("rfid_hopeland_ip_two")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Username"
                                        value={settings.rfid_hopeland_username_two || ""}
                                        onChange={handleChange("rfid_hopeland_username_two")}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="Password"
                                        value={settings.rfid_hopeland_password_two || ""}
                                        onChange={handleChange("rfid_hopeland_password_two")}
                                    />
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Environment Tab (Read-only) */}
                        <TabPanel value={tabValue} index={5}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {t("MIZAN_SETTINGS.environment_info") || "معلومات البيئة (للقراءة فقط)"}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.app_version") || "إصدار التطبيق"}
                                        value={settings.app_version || "-"}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.windows_os_version") || "إصدار Windows"}
                                        value={settings.windows_os_version || "-"}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.java_version") || "إصدار Java"}
                                        value={settings.java_version || "-"}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.sql_server_version") || "إصدار SQL Server"}
                                        value={settings.sql_server_version || "-"}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.dotnet_framework_version") || "إصدار .NET"}
                                        value={settings.dotnet_framework_version || "-"}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={t("MIZAN_SETTINGS.sql_client_version") || "إصدار SQL Client"}
                                        value={settings.sql_client_version || "-"}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="textSecondary">
                                        {settings.last_environment_sync
                                            ? `${t("MIZAN_SETTINGS.last_sync") || "آخر تحديث"}: ${new Date(settings.last_environment_sync).toLocaleString()}`
                                            : t("MIZAN_SETTINGS.never_synced") || "لم يتم المزامنة بعد"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </TabPanel>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button onClick={handleClose} variant="outlined" color="inherit">
                    {t("GLOBAL.action_cancel") || "إلغاء"}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={saving || loading}
                >
                    {t("GLOBAL.action_save") || "حفظ"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
